import { uniqueId } from 'lodash-es';
import type {
  AreaConfigResponse,
  AreaSelection,
  CurvePoint,
  CurveSchedule,
  CurveTail,
  DifferentialScenarioFormData,
  DifferentialScenarioResponse,
  EscalationConfig,
  EscalationDirection,
  EscalationPeriod,
  GeometryType,
  LineItem,
  LineItemResponse,
  StepSegment,
  TailKind,
} from './types';

export type FormMode = 'create' | 'edit';

export const GLOBAL_SELECTION_ID = 'global';

// ─────────────────────────────────────
// Constants
// ─────────────────────────────────────

export const DEFAULT_GEOMETRY_PRIORITY = [
  'Slot',
  'Unit',
  'Geology',
  'AOI',
  'TITLE',
  'StructureGrid',
];

// ─────────────────────────────────────
// Factories / defaults
// ─────────────────────────────────────

/**
 * Builds a fresh escalation config with sensible defaults.
 * @return {EscalationConfig} A new escalation config
 */
export function blankEscalation(): EscalationConfig {
  return {
    direction: 'ascending',
    period: 'annually',
    periodCount: 1,
    pctPerPeriod: '',
  };
}

/**
 * Builds a fresh open-ended stepped segment.
 * @return {StepSegment} A new segment
 */
export function blankSegment(): StepSegment {
  return { id: Number(uniqueId()), durationMonths: null, value: '' };
}

/**
 * Builds a blank line item defaulting to the flat profile.
 * @return {LineItem} A new line item
 */
export function blankLineItem(): LineItem {
  return {
    id: Number(uniqueId()),
    product: '',
    valueType: 'flat',
    profile: 'flat',
    value: '',
    notes: '',
    escalation: blankEscalation(),
    segments: [blankSegment()],
  };
}

/**
 * Builds a blank curve schedule with no points and a Hold Flat tail (BE-required default).
 * @return {CurveSchedule} A new curve schedule
 */
export function blankCurve(): CurveSchedule {
  return { points: [], tail: { kind: 'hold_flat' } };
}

/**
 * Builds the default two-segment list used when the user first switches a custom
 * curve's tail kind to Stepped: one explicit 12-month segment + a remainder
 * segment, both anchored on the supplied last-curve-point value.
 * @param {number} anchorValue - Value to use for both segments
 * @return {StepSegment[]} A new segment list (length 2)
 */
export function defaultCurveTailSegments(anchorValue: number): StepSegment[] {
  return [
    { id: Number(uniqueId()), durationMonths: 12, value: anchorValue },
    { id: Number(uniqueId()), durationMonths: null, value: anchorValue },
  ];
}

export const DEFAULT_AREA_SELECTION: Omit<AreaSelection, 'id'> = {
  status: 'new',
  geometryType: 'area',
  configurableId: null,
  category: '',
  areaName: '',
  unitType: '',
  unitName: '',
  slotUwi: '',
  slotName: '',
  alias: '',
  notes: '',
  lineItems: [],
};

export const DEFAULT_FORM_VALUES: DifferentialScenarioFormData = {
  global: {
    name: '',
    effectiveDate: '',
    description: '',
    notes: '',
    geometryPriority: [...DEFAULT_GEOMETRY_PRIORITY],
  },
  areas: [],
};

// ─────────────────────────────────────
// Display helpers
// ─────────────────────────────────────

/**
 * Returns the display name for an area based on alias / area / unit / slot name.
 * @param {AreaSelection} area - The area selection
 * @return {string} The display name
 */
export function getAreaDisplayName(area: AreaSelection): string {
  return (
    area.alias || area.areaName || area.unitName || area.slotName || 'New Area'
  );
}

/**
 * Returns the nav badge label based on geometry type and category/unitType.
 * @param {AreaSelection} area - The area selection
 * @return {string | undefined} The badge label
 */
export function getAreaBadge(area: AreaSelection): string | undefined {
  if (area.geometryType === 'slot') return 'Slot';
  if (area.geometryType === 'unit') return area.unitType || undefined;
  return area.category || undefined;
}

// ─────────────────────────────────────
// Numeric helpers
// ─────────────────────────────────────

/**
 * Returns true when a value is a finite number (rejects '' and null).
 * @param {unknown} value - The value to test
 * @return {boolean} True if finite numeric
 */
function isFiniteNumber(value: unknown): boolean {
  return typeof value === 'number' && Number.isFinite(value);
}

/**
 * Returns true when a value is a positive integer (>= 1).
 * @param {unknown} value - The value to test
 * @return {boolean} True if a positive integer
 */
function isPositiveInteger(value: unknown): boolean {
  return typeof value === 'number' && Number.isInteger(value) && value >= 1;
}

// ─────────────────────────────────────
// Validators
// ─────────────────────────────────────

/**
 * Checks whether an area selection has all required geometry fields filled.
 * @param {AreaSelection} area - The area to validate
 * @return {boolean} True if the area geometry selection is complete
 */
export function isAreaSelectionComplete(area: AreaSelection): boolean {
  if (area.geometryType === 'area') {
    return !!area.category && !!area.areaName;
  }
  if (area.geometryType === 'unit') {
    return !!area.unitType && !!area.unitName;
  }
  return !!area.slotUwi;
}

/**
 * Validates an escalation config (direction/period/periodCount/pctPerPeriod).
 * @param {EscalationConfig} e - The escalation config
 * @return {boolean} True if valid
 */
export function isEscalationValid(e: EscalationConfig): boolean {
  if (e.direction !== 'ascending' && e.direction !== 'descending') return false;
  if (e.period !== 'monthly' && e.period !== 'annually') return false;
  if (!isPositiveInteger(e.periodCount)) return false;
  return isFiniteNumber(e.pctPerPeriod);
}

/**
 * True when a segment's duration is open-ended (null or blank).
 * @param {StepSegment} seg - The segment to test
 * @return {boolean} True if the segment has no finite duration
 */
function isOpenEnded(seg: StepSegment): boolean {
  return seg.durationMonths === null || seg.durationMonths === '';
}

/**
 * Validates a stepped schedule's segments: at least one segment, every value
 * numeric, durations positive integers, and the final segment open-ended while
 * all others are finite.
 * @param {StepSegment[]} segments - The segments
 * @return {boolean} True if valid
 */
export function areSegmentsValid(segments: StepSegment[]): boolean {
  if (segments.length === 0) return false;
  const lastIndex = segments.length - 1;
  // The terminal segment must be open-ended; this also rejects a lone finite
  // segment, which would otherwise reach the backend as a non-open-ended tail.
  if (!isOpenEnded(segments[lastIndex])) return false;
  return segments.every((seg, i) => {
    if (!isFiniteNumber(seg.value)) return false;
    if (isOpenEnded(seg)) {
      return i === lastIndex; // only the last may be open-ended
    }
    return isPositiveInteger(seg.durationMonths);
  });
}

/**
 * Checks whether a line item is valid per the backend's profile/value rules.
 * The `custom` profile is treated as valid only as a loaded pass-through.
 * @param {LineItem} li - The line item
 * @return {boolean} True if valid
 */
export function isLineItemValid(li: LineItem): boolean {
  if (!li.product || !li.valueType || !li.profile) return false;
  if (li.product === 'ngl' && li.valueType !== 'percent') return false;

  switch (li.profile) {
    case 'flat':
      return isFiniteNumber(li.value);
    case 'escalating':
      return isFiniteNumber(li.value) && isEscalationValid(li.escalation);
    case 'stepped':
      return areSegmentsValid(li.segments);
    case 'custom':
      if (!li.curve || li.curve.points.length === 0) return false;
      if (!li.curve.tail) return false;
      if (li.curve.tail.kind === 'escalating') {
        return isEscalationValid(li.curve.tail.escalation ?? blankEscalation());
      }
      if (li.curve.tail.kind === 'stepped') {
        return areSegmentsValid(li.curve.tail.segments ?? []);
      }
      return true; // hold_flat needs no further config
    default:
      return false;
  }
}

/**
 * Checks for duplicate products within an area's line items (one per product).
 * @param {LineItem[]} items - The line items
 * @return {boolean} True if duplicates exist
 */
export function hasDuplicateProducts(items: LineItem[]): boolean {
  const seen = new Set<string>();
  for (const li of items) {
    if (!li.product) continue;
    if (seen.has(li.product)) return true;
    seen.add(li.product);
  }
  return false;
}

// ─────────────────────────────────────
// Configurable type mapping
// ─────────────────────────────────────

/**
 * Maps a frontend geometry type to the backend configurable_type string.
 * @param {GeometryType} geometryType - The frontend geometry type
 * @return {string} The backend configurable_type
 */
function toConfigurableType(geometryType: GeometryType): string {
  switch (geometryType) {
    case 'area':
      return 'AreaGeometry';
    case 'unit':
      return 'DevelopmentArea';
    case 'slot':
      return 'Well';
  }
}

/**
 * Maps a backend configurable_type to the frontend geometry type.
 * @param {string} configurableType - The backend configurable_type
 * @return {GeometryType} The frontend geometry type
 */
function fromConfigurableType(configurableType: string): GeometryType {
  switch (configurableType) {
    case 'DevelopmentArea':
      return 'unit';
    case 'Well':
      return 'slot';
    default:
      return 'area';
  }
}

// ─────────────────────────────────────
// Payload: Form → Backend (snake_case)
// ─────────────────────────────────────

/**
 * Builds the snake_case `config` object the backend expects for a line item,
 * dispatched on the profile.
 * @param {LineItem} li - The line item
 * @return {Record<string, unknown>} The config payload
 */
function buildLineItemConfig(li: LineItem): Record<string, unknown> {
  switch (li.profile) {
    case 'escalating':
      return {
        direction: li.escalation.direction,
        period: li.escalation.period,
        period_count: Number(li.escalation.periodCount),
        pct_per_period: Number(li.escalation.pctPerPeriod),
      };
    case 'stepped':
      return {
        steps: li.segments.map((seg) => ({
          duration_months:
            seg.durationMonths === '' || seg.durationMonths === null
              ? null
              : Number(seg.durationMonths),
          value: Number(seg.value),
        })),
      };
    case 'custom':
      return buildCustomConfig(li.curve);
    case 'flat':
    default:
      return {};
  }
}

/**
 * Builds the backend `config` payload for a custom-profile line item: `points`
 * + `tail` in snake_case, BE-shape exact. Returns an empty object when the
 * caller wires a save before the user has imported a curve (validator should
 * have blocked this, but defend anyway).
 * @param {CurveSchedule | undefined} curve - The form's curve schedule
 * @return {Record<string, unknown>} The config payload
 */
function buildCustomConfig(
  curve: CurveSchedule | undefined
): Record<string, unknown> {
  if (!curve) return {};
  const tail = curve.tail ?? { kind: 'hold_flat' as TailKind };

  const tailPayload: Record<string, unknown> = { kind: tail.kind };
  if (tail.kind === 'escalating' && tail.escalation) {
    tailPayload.escalation = {
      direction: tail.escalation.direction,
      period: tail.escalation.period,
      period_count: Number(tail.escalation.periodCount),
      pct_per_period: Number(tail.escalation.pctPerPeriod),
    };
  } else if (tail.kind === 'stepped' && tail.segments) {
    tailPayload.segments = tail.segments.map((seg) => ({
      duration_months:
        seg.durationMonths === '' || seg.durationMonths === null
          ? null
          : Number(seg.durationMonths),
      value: Number(seg.value),
    }));
  }

  return {
    points: curve.points.map((point) => ({
      date: point.date,
      value: point.value,
    })),
    tail: tailPayload,
  };
}

/**
 * Maps a line item to the backend nested-attributes shape. Only includes `id`
 * for items that came from the server (in originalIds) so Rails creates new ones.
 * @param {LineItem} li - The line item
 * @param {number[]} originalIds - Server-assigned IDs from the initial load
 * @return {Record<string, unknown>} Backend line item payload
 */
function mapLineItemToPayload(li: LineItem, originalIds: number[]) {
  const hasScalarValue = li.profile === 'flat' || li.profile === 'escalating';
  const payload: Record<string, unknown> = {
    product: li.product,
    value_type: li.valueType,
    profile: li.profile,
    value: hasScalarValue && li.value !== '' ? Number(li.value) : null,
    notes: li.notes || null,
    config: buildLineItemConfig(li),
  };
  if (originalIds.includes(li.id)) payload.id = li.id;

  const supportingFile = buildSupportingFileAttributes(li);
  if (supportingFile) payload.supporting_file_attributes = supportingFile;

  return payload;
}

/**
 * Builds the `supporting_file_attributes` nested-attributes payload for a line
 * item. Three cases:
 *   - User uploaded a fresh CSV this session (`supportingFileSignedId` set):
 *       in CREATE: `{ attachment: <signed_id> }`
 *       in REPLACE: `{ id: <existing.id>, attachment: <signed_id> }` (BE uses
 *       `update_only: true` so this swaps the attachment in place)
 *   - User switched profile away from custom on a row that had a server-loaded
 *     supporting file (`_destroySupportingFile` flag): `{ id, _destroy: true }`
 *   - Untouched custom row: returns null, payload omits the key, BE leaves the
 *     existing record alone.
 * @param {LineItem} li - The form line item
 * @return {Record<string, unknown> | null} The nested-attributes payload or null
 */
function buildSupportingFileAttributes(
  li: LineItem
): Record<string, unknown> | null {
  if (li._destroySupportingFile && li.supportingFile) {
    return { id: li.supportingFile.id, _destroy: true };
  }
  if (li.supportingFileSignedId) {
    const attrs: Record<string, unknown> = {
      attachment: li.supportingFileSignedId,
    };
    if (li.supportingFile) attrs.id = li.supportingFile.id;
    return attrs;
  }
  return null;
}

/**
 * Maps an area to the backend shape for CREATE (no entry IDs needed).
 * @param {AreaSelection} area - The area selection
 * @return {Record<string, unknown>} Backend area config payload
 */
function mapNewAreaToPayload(area: AreaSelection) {
  return {
    configurable_type: toConfigurableType(area.geometryType),
    configurable_id: area.configurableId,
    alias_name: area.alias || null,
    notes: area.notes || null,
    line_items_attributes: area.lineItems.map((li) =>
      mapLineItemToPayload(li, [])
    ),
  };
}

/**
 * Builds the full payload for creating a new scenario (POST).
 * @param {DifferentialScenarioFormData} values - The form values
 * @return {object} The wrapped payload
 */
export function buildCreatePayload(values: DifferentialScenarioFormData) {
  const { global: g } = values;
  return {
    differential_scenario: {
      name: g.name,
      effective_date: g.effectiveDate || null,
      description: g.description || null,
      notes: g.notes || null,
      geometry_priority: g.geometryPriority,
      area_configs_attributes: values.areas.map(mapNewAreaToPayload),
    },
  };
}

/**
 * Builds a diff payload for updating an existing scenario (PATCH).
 * Saved areas keep server IDs on their line items, compute `_destroy` for
 * removed items, and omit IDs on newly added ones. New areas send everything
 * without IDs; destroyed areas send `{ id, _destroy: true }`.
 * @param {DifferentialScenarioFormData} values - Current form values
 * @param {number[]} removedAreaIds - Backend IDs of deleted areas
 * @return {object} The wrapped payload
 */
export function buildUpdatePayload(
  values: DifferentialScenarioFormData,
  removedAreaIds: number[]
) {
  const { global: g } = values;
  const areaConfigsAttrs: Record<string, unknown>[] = [];

  for (const id of removedAreaIds) {
    areaConfigsAttrs.push({ id, _destroy: true });
  }

  for (const area of values.areas) {
    if (area.status === 'new') {
      areaConfigsAttrs.push(mapNewAreaToPayload(area));
      continue;
    }

    const origIds = area._originalLineItemIds ?? [];
    const lineItemsAttrs: Record<string, unknown>[] = area.lineItems.map((li) =>
      mapLineItemToPayload(li, origIds)
    );

    const currentIds = new Set(
      area.lineItems.filter((li) => origIds.includes(li.id)).map((li) => li.id)
    );
    for (const id of origIds) {
      if (!currentIds.has(id)) lineItemsAttrs.push({ id, _destroy: true });
    }

    areaConfigsAttrs.push({
      id: area.id,
      configurable_type: toConfigurableType(area.geometryType),
      configurable_id: area.configurableId,
      alias_name: area.alias || null,
      notes: area.notes || null,
      line_items_attributes: lineItemsAttrs,
    });
  }

  return {
    differential_scenario: {
      name: g.name,
      effective_date: g.effectiveDate || null,
      description: g.description || null,
      notes: g.notes || null,
      geometry_priority: g.geometryPriority,
      area_configs_attributes: areaConfigsAttrs,
    },
  };
}

// ─────────────────────────────────────
// Response mapper: Backend → Form (camelCase)
// ─────────────────────────────────────

/**
 * Maps a backend line item response into the form's LineItem shape, restoring
 * profile-specific editor state from the camelCased config.
 * @param {LineItemResponse} li - The line item from the API response
 * @return {LineItem} The form line item
 */
function mapLineItemResponse(li: LineItemResponse): LineItem {
  const base = blankLineItem();
  const config = (li.config ?? {}) as Record<string, unknown>;

  const item: LineItem = {
    ...base,
    id: li.id,
    product: li.product,
    valueType: li.valueType,
    profile: li.profile,
    value: li.value ?? '',
    notes: li.notes ?? '',
    supportingFile: li.supportingFile,
  };

  if (li.profile === 'escalating') {
    item.escalation = {
      direction:
        (config.direction as EscalationConfig['direction']) ?? 'ascending',
      period: (config.period as EscalationConfig['period']) ?? 'annually',
      periodCount: (config.periodCount as number) ?? 1,
      pctPerPeriod: (config.pctPerPeriod as number) ?? '',
    };
  } else if (li.profile === 'stepped') {
    const steps = Array.isArray(config.steps)
      ? (config.steps as { durationMonths: number | null; value: number }[])
      : [];
    item.segments = steps.length
      ? steps.map((s) => ({
          id: Number(uniqueId()),
          durationMonths: s.durationMonths,
          value: s.value,
        }))
      : [blankSegment()];
  } else if (li.profile === 'custom') {
    item.curve = hydrateCurveFromConfig(config, li.supportingFile);
    item.value = '';
  }

  return item;
}

/**
 * Hydrates a typed `CurveSchedule` from the BE's camelCased `config` blob for
 * a custom-profile line item. Tail kind-specific config (`escalation`,
 * `segments`) is rebuilt from the response — the inverse of buildCustomConfig.
 * Defensive against partial/missing shapes since the BE validates writes, but
 * old data and edge cases shouldn't crash the editor.
 * @param {Record<string, unknown>} config - The line item's response.config (camelCased)
 * @param {{ fileName: string } | null | undefined} supportingFile - Loaded file metadata, used as the display source filename
 * @return {CurveSchedule} The hydrated curve
 */
function hydrateCurveFromConfig(
  config: Record<string, unknown>,
  supportingFile: { fileName: string } | null | undefined
): CurveSchedule {
  const points: CurvePoint[] = Array.isArray(config.points)
    ? (config.points as Array<{ date: string; value: number }>).map((p) => ({
        date: p.date,
        value: p.value,
      }))
    : [];

  const tailRaw = (config.tail as Record<string, unknown> | undefined) ?? {
    kind: 'hold_flat',
  };
  const tail: CurveTail = {
    kind: (tailRaw.kind as TailKind) ?? 'hold_flat',
  };

  if (tail.kind === 'escalating' && tailRaw.escalation) {
    const escRaw = tailRaw.escalation as Record<string, unknown>;
    tail.escalation = {
      direction: (escRaw.direction as EscalationDirection) ?? 'ascending',
      period: (escRaw.period as EscalationPeriod) ?? 'annually',
      periodCount: (escRaw.periodCount as number) ?? 1,
      pctPerPeriod: (escRaw.pctPerPeriod as number) ?? '',
    };
  } else if (tail.kind === 'stepped' && Array.isArray(tailRaw.segments)) {
    tail.segments = (
      tailRaw.segments as Array<{
        durationMonths: number | null;
        value: number;
      }>
    ).map((s) => ({
      id: Number(uniqueId()),
      durationMonths: s.durationMonths,
      value: s.value,
    }));
  }

  return {
    source: supportingFile?.fileName,
    points,
    tail,
  };
}

/**
 * Maps a backend area config response to the form's AreaSelection shape.
 * @param {AreaConfigResponse} ac - The area config from API response
 * @return {AreaSelection} The form area selection
 */
function mapAreaConfigToSelection(ac: AreaConfigResponse): AreaSelection {
  const geometryType = fromConfigurableType(ac.configurableType);
  const isArea = geometryType === 'area';
  const isUnit = geometryType === 'unit';
  const isSlot = geometryType === 'slot';

  return {
    id: ac.id,
    status: 'saved',
    geometryType,
    configurableId: ac.configurableId,
    category: isArea ? (ac.configurableCategory ?? '') : '',
    areaName: isArea ? (ac.configurableDisplayName ?? '') : '',
    unitType: isUnit ? (ac.configurableUnitType ?? '') : '',
    unitName: isUnit ? (ac.configurableDisplayName ?? '') : '',
    slotUwi: isSlot ? (ac.configurableDisplayName?.split(' — ')[0] ?? '') : '',
    slotName: isSlot ? (ac.configurableDisplayName ?? '') : '',
    alias: ac.aliasName ?? '',
    notes: ac.notes ?? '',
    lineItems: ac.lineItems.map(mapLineItemResponse),
    _originalLineItemIds: ac.lineItems.map((li) => li.id),
  };
}

/**
 * Converts a backend scenario response into form data and geometries.
 * @param {DifferentialScenarioResponse} res - The API response
 * @return {{ formData: DifferentialScenarioFormData, geometries: Record<number, GeoJSON.Geometry> }}
 */
export function mapResponseToFormData(res: DifferentialScenarioResponse): {
  formData: DifferentialScenarioFormData;
  geometries: Record<number, GeoJSON.Geometry>;
} {
  const geometries: Record<number, GeoJSON.Geometry> = {};

  const areas = res.areaConfigs.map((ac) => {
    const selection = mapAreaConfigToSelection(ac);
    if (ac.configurableGeojson) {
      const geojson = ac.configurableGeojson;
      geometries[ac.id] =
        'geometry' in geojson && geojson.geometry
          ? geojson.geometry
          : (geojson as unknown as GeoJSON.Geometry);
    }
    return selection;
  });

  return {
    formData: {
      global: {
        name: res.name,
        effectiveDate: res.effectiveDate ?? '',
        description: res.description ?? '',
        notes: res.notes ?? '',
        geometryPriority: res.geometryPriority,
      },
      areas,
    },
    geometries,
  };
}
